import { useState, FormEvent, ChangeEvent } from 'react';
import {
  USE_CASE_OPTIONS,
  UseCase,
  AccessRequestBody,
  isValidEmail,
  isValidName,
  isValidAdditionalDetails,
} from '../api/access';

interface BetaAccessFormProps {
  onSubmit: (data: AccessRequestBody) => Promise<void>;
  disabled?: boolean;
  initialEmail?: string;
}

export default function BetaAccessForm({ onSubmit, disabled = false, initialEmail = '' }: BetaAccessFormProps) {
  const [email, setEmail] = useState(initialEmail);
  const [name, setName] = useState('');
  const [useCase, setUseCase] = useState<UseCase>('Academic');
  const [additionalDetails, setAdditionalDetails] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validateField = (field: string, value: string): string | null => {
    switch (field) {
      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!isValidEmail(value)) return 'Invalid email format';
        return null;
      case 'name':
        if (!value.trim()) return 'Name is required';
        if (!isValidName(value)) return 'Name must be 1-100 characters';
        return null;
      case 'additionalDetails':
        if (value && !isValidAdditionalDetails(value)) {
          return 'Additional details must be 1000 characters or less';
        }
        return null;
      default:
        return null;
    }
  };

  const handleBlur = (field: string) => {
    setTouched({ ...touched, [field]: true });
    const value = field === 'email' ? email : field === 'name' ? name : additionalDetails;
    const error = validateField(field, value);
    if (error) {
      setErrors({ ...errors, [field]: error });
    } else {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
    }
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (touched.email) {
      const error = validateField('email', e.target.value);
      if (error) {
        setErrors({ ...errors, email: error });
      } else {
        const newErrors = { ...errors };
        delete newErrors.email;
        setErrors(newErrors);
      }
    }
  };

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (touched.name) {
      const error = validateField('name', e.target.value);
      if (error) {
        setErrors({ ...errors, name: error });
      } else {
        const newErrors = { ...errors };
        delete newErrors.name;
        setErrors(newErrors);
      }
    }
  };

  const handleAdditionalDetailsChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setAdditionalDetails(e.target.value);
    if (touched.additionalDetails) {
      const error = validateField('additionalDetails', e.target.value);
      if (error) {
        setErrors({ ...errors, additionalDetails: error });
      } else {
        const newErrors = { ...errors };
        delete newErrors.additionalDetails;
        setErrors(newErrors);
      }
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Validate all fields
    const emailError = validateField('email', email);
    const nameError = validateField('name', name);
    const detailsError = validateField('additionalDetails', additionalDetails);

    const newErrors: Record<string, string> = {};
    if (emailError) newErrors.email = emailError;
    if (nameError) newErrors.name = nameError;
    if (detailsError) newErrors.additionalDetails = detailsError;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched({ email: true, name: true, additionalDetails: true });
      return;
    }

    // Submit form with trimmed values
    await onSubmit({
      email: email.trim(),
      name: name.trim(),
      useCase,
      additionalDetails: additionalDetails.trim() || undefined,
    });
  };

  const remainingChars = 1000 - additionalDetails.length;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Email Field */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
          Email Address <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="email"
          value={email}
          onChange={handleEmailChange}
          onBlur={() => handleBlur('email')}
          disabled={disabled}
          className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
            errors.email && touched.email ? 'border-red-500' : ''
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          placeholder="you@example.com"
        />
        {errors.email && touched.email && (
          <p className="mt-1 text-sm text-red-600">{errors.email}</p>
        )}
      </div>

      {/* Name Field */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
          Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="name"
          value={name}
          onChange={handleNameChange}
          onBlur={() => handleBlur('name')}
          disabled={disabled}
          className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
            errors.name && touched.name ? 'border-red-500' : ''
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          placeholder="Your full name"
        />
        {errors.name && touched.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name}</p>
        )}
      </div>

      {/* Use Case Dropdown */}
      <div>
        <label htmlFor="useCase" className="block text-sm font-medium text-gray-700">
          What will you use SmartPrompt for? <span className="text-red-500">*</span>
        </label>
        <select
          id="useCase"
          value={useCase}
          onChange={(e) => setUseCase(e.target.value as UseCase)}
          disabled={disabled}
          className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : ''
          }`}
        >
          {USE_CASE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      {/* Additional Details */}
      <div>
        <label htmlFor="additionalDetails" className="block text-sm font-medium text-gray-700">
          Tell us more (optional)
        </label>
        <textarea
          id="additionalDetails"
          value={additionalDetails}
          onChange={handleAdditionalDetailsChange}
          onBlur={() => handleBlur('additionalDetails')}
          disabled={disabled}
          rows={4}
          className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
            errors.additionalDetails && touched.additionalDetails ? 'border-red-500' : ''
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          placeholder="Tell us about your use case or why you're interested in SmartPrompt..."
        />
        <div className="mt-1 flex justify-between items-center">
          {errors.additionalDetails && touched.additionalDetails && (
            <p className="text-sm text-red-600">{errors.additionalDetails}</p>
          )}
          <p className={`text-sm ${remainingChars < 100 ? 'text-orange-600' : 'text-gray-500'} ml-auto`}>
            {remainingChars} characters remaining
          </p>
        </div>
      </div>

      {/* Submit Button */}
      <div>
        <button
          type="submit"
          disabled={disabled || Object.keys(errors).length > 0}
          className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
            disabled || Object.keys(errors).length > 0
              ? 'opacity-50 cursor-not-allowed'
              : ''
          }`}
        >
          {disabled ? 'Submitting...' : 'Request Beta Access'}
        </button>
      </div>
    </form>
  );
}
